<template>
	<row class="section-gradient">
		<container>
			<div class="flex flex-col items-center justify-center">
				<h2
					class="gradient-title-white text-[50px] lg:text-[54px] font-medium text-center mb-[32px] lg:mb-[72px]"
				>
					Frequently Asked Questions
				</h2>
				<div class="w-full md:max-w-4xl flex flex-col gap-4">
					<div
						v-for="(faq, index) in faqs"
						:key="index"
						class="faq-item"
						:class="activeFaqIndex === index ? 'active' : 'inactive'"
					>
						<div
							@click="toggleFaq(index)"
							class="faq-question flex justify-between items-center w-full"
						>
							{{ faq.question }}
							<span
								:class="{ 'rotate-180': activeFaqIndex === index }"
								class="chevron-icon"
							>
								<img
									src="/images/CaretDown.svg"
									alt="Chevron Down Icon"
								/>
							</span>
						</div>

						<div
							class="faq-answer pt-[16px]"
							v-html="faq.answer"
						></div>
					</div>
				</div>

				<div
					class="mt-8 px-4 py-3 rounded-xl border border-white flex items-center gap-3"
				>
					<img
						src="/images/ph_globe.png"
						alt=""
					/>
					<a
						href="https://support.stockstotrade.com/"
						class="text-lg font-medium text-white"
					>
						Visit Our Support Page
					</a>
				</div>
			</div>
		</container>
	</row>
</template>

<script setup>
const faqs = [
	{
		question: "Is StocksToTrade a broker?",
		answer:
			"StocksToTrade is a real-time trading platform. We have broker integration on our platform! If your broker is a qualifying broker, you can integrate it and trade on the STT platform. Here’s a link on how to set up your broker account on STT: <a class='text-blue-300' href='https://support.stockstotrade.com/hc/en-us/articles/115002765607-Is-StocksToTrade-a-broker' target='_blank'>https://support.stockstotrade.com/hc/en-us/articles/115002765607-Is-StocksToTrade-a-broker</a>"
	},
	{
		question: "Does the trial include all of the features?",
		answer:
			"The trial offers many features and access to Nasdaq Last Sale data. When you subscribe, you have the option to add our additional features such as the chatroom, level 2 or tip ranks!"
	},
	{
		question: "Do I have to download and install software?",
		answer:
			'Yes, please see our system requirements at <a class="text-blue-300" target="_blank" href="https://stockstotrade.com/system-requirements/">https://stockstotrade.com/system-requirements/</a>'
	},
	{
		question: "What Add On Features does StocksToTrade offer?",
		answer: `<div class="text-center">
                    <p class="text-lg font-bold">StocksToTrade offers the following Add On Features that you can add to your subscription at any time via your account dashboard located here: 
						<p class='text-base'><a class="text-blue-300" href="https://orders.stockstotrade.com/cp" target="_blank" rel="noopener noreferrer">Orders StockToTrade</a></p>
					</p>
                    <p class="text-lg font-bold">Level 2 Data</p>
                    <p class='text-base'><strong>$29/Monthly &amp; $345/Annually</strong></p>
                    <p class='text-base'>Level 2 will give you the best and all bid and ask prices for a given stock in real-time. It will also provide you with the different Market Markers and IDs. Level 2 will also include a 5 count bar that shows you the live-action of bids and asks coming in, in real-time! Knowing exactly who has an interest in a stock can be extremely useful for any style of trading!&nbsp;</p>
                    <p class='text-base'><a class="text-blue-300" target='_blank' href="https://orders.stockstotrade.com/cp/add_ons" target="_blank" rel="noopener noreferrer">Orders Add Ons</a></p>
                    <p class='text-base'>Note that this add-on feature is only available to current monthly or annual subscribers to STT.&nbsp;</p>
                    <p class="text-lg font-bold">Breaking News Chat</p>
                    <p class='text-base'><strong>$49/Monthly &amp; $490/Annually</strong></p>
                    <p class='text-base'>The Breaking News Chat feature is a chatroom on the StocksToTrade platform where our analysts provide relevant breaking news in real-time. Their goal is to help our subscribers gain access to breaking news articles before they are released or the stock has reacted. As former wall Streeters, our analysts also interact with our subscribers, answering and engaging in the chat when they are not breaking news.&nbsp;</p>
                    <p class='text-base'>For a video, more information, and a trial offer, please visit: <a class="text-blue-300" href="https://stockstotrade.com/breakingnewschat/" target="_blank" rel="noopener noreferrer">StocksToTrade Breaking News</a></p>
                    <p class="text-lg font-bold">Tip Ranks</p>
                    <p class='text-base'><strong>$8.95/Monthly &amp; $89.50/Annually</strong></p>
                    <p class='text-base'>Tip Ranks is a tool used to give you insight into different stocks in the market. It provides you with the Analyst Rating, Insider Rating, and Hedge Fund Rating. Based on these ratings, you will be able to see if the stock is currently a Strong Buy, Buy, Neutral, Sell, or Strong Sell.&nbsp;</p>
                    <p class='text-base'>Learn more about this tool here: <a class="text-blue-300" href="https://university.stockstotrade.com/tipranks/" target="_blank" rel="noopener noreferrer">StocksToTrade University</a></p>
                    <p class="text-lg font-bold">Small Cap Rockets</p>
                    <p class='text-base'><strong>$50/Monthly &amp; $500/Annually</strong></p>
                    <p class='text-base'>Small-Cap Rockets is a room in the StocksToTrade platform where our experienced traders Matt Monaco and Bryce Tuohey share the stocks they are trading themselves! The information provided will allow you to see not only the stocks they are trading but how our experienced traders get in and out of stock positions.&nbsp;</p>
                    <p class='text-base'>For a trial and to learn more about our experienced traders, please visit: <a class="text-blue-300" href="https://stockstotrade.com/small-cap-rockets/" target='_blank'>Small Cap Rockets</a></p>
                </div>
                `
	},
	{
		question: "What type of person should buy this?",
		answer:
			"<p class='text-base'>All traders; beginners, intermediate, or advanced. We have a great tool that allows all traders to find potentially profitable moves. It can be used in a basic manner while learning, medium use when intermediate, and even as an advanced screener customizer and indicators for advanced traders.</p>"
	},
	{
		question: "What is Oracle?",
		answer: `<div class="article-body">
                  <p class='text-base'><span>To get the full breakdown of the <em>Oracle Algorithm Scanner</em> please visit this article <a class="text-blue-300" href="https://support.stockstotrade.com/hc/en-us/articles/12761370640023-Breakdown-of-the-Oracle-Algorithm-Scanner" target="_blank" rel="noopener">here.</a></span></p>
                  <p class='text-base'><span>Oracle is a prediction algorithm scanner that finds stocks that have potential for movement in the market during the trading day. It gives Support and Resistance indicators for the stock and suggested signals to watch out for buying or selling. You will also have </span><span>Oracle Levels for each of the selected stocks that establishes resistance and support levels in order to quickly evaluate a security's potential for momentum throughout the trading session.</span></p>
                  <p class='text-base'><span>For more information, here’s a video link to help you learn more about STT’s Oracle feature:</span></p>
				  <p class='text-base'><span><a class="text-blue-300" href="https://www.youtube.com/user/stockstotrade/search?query=Oracle" target="_blank">https://youtu.be/stockstotrade/oracle</a></span></p>
                 </div>
                `
	},
	{
		question: "What is StocksToTrade?",
		answer: `<div class="article-body">
                  <p class='text-base'><span>StocksToTrade is a real-time stock scanner intended to quickly find potential stock opportunities based on a wide variety of criteria. </span><span>Some of our features include: </span></p>
                    <ul>
                    <li><span>Super-fast, real-time Level 1 and Level 2 (optional) for all U.S. equities</span></li>
                    <li><span>All major U.S. markets: NASDAQ, NYSE, AMEX and OTC markets</span></li>
                    <li><span>Powerful strategy scans</span></li>
                    <li><span>Dynamic charts with news indicators</span></li>
                    <li><span>Chart drawing tools, indicators, and studies</span></li>
                    <li><span>Price-event alerts on charts</span></li>
                    <li><span>Real-time, criteria-based news streamer</span></li>
                    <li><span>Watchlist-filtered news</span></li>
                    <li><span>SEC filings streamer</span></li>
                    <li><span>Stock-specific SEC filings and news</span></li>
                    <li><span>Unlimited watchlists</span></li>
                    <li><span>Criteria-based, custom stock screener</span></li>
                    <li><span>Powerful pre-programmed scans based on Timothy Sykes’ strategies</span></li>
                    <li><span>Quick access to top % gainers and losers</span></li>
                    <li><span>Quick access to new highs and Lows</span></li>
                    <li><span>Filterable previous-day data for research</span></li>
                    <li><span>User settable stock alerts</span></li>
                    <li><span>Linked information for efficiency</span></li>
                    <li><span>Scroll through your existing watchlists fast</span></li>
                    <li><span>Quickly find related companies in the same industry</span></li>
                    <li><span>Key stock statistics at your fingertips</span></li>
                    <li><span>Company profile with basic stock information</span></li>
                    <li><span>Paper trading for practicing without risking capital</span></li>
                    <li><span>And more! </span></li>
                    </ul>
                    <p class='text-base'><span>This makes StocksToTrade an invaluable tool for improving your trading efficiency and research process.</span></p>
                 </div>
                `
	},
	{
		question: "Can I use STT if I’m outside of the U.S.?",
		answer:
			"<p class='text-base'>StocksToTrade provides service to traders worldwide, with access to U.S. equity market data in real time. We don’t have geographical restrictions and welcome users from around the globe to use our services for their market data needs.</p>"
	},
	{
		question: "Does StocksToTrade provide real-time stock market data?",
		answer:
			"<p class='text-base'>Yes. STT is a fast, real-time Level 1 and Level 2 platform for all U.S. equities. All features offered on STT are provided in real time.</p>"
	}
];

const activeFaqIndex = ref(null);

const toggleFaq = (index) => {
	if (activeFaqIndex.value === index) {
		activeFaqIndex.value = null;
	} else {
		activeFaqIndex.value = index;
	}
};
</script>

<style lang="scss">
@use "~/assets/utils.scss" as *;

.faq-item {
	position: relative;
	margin-bottom: 1rem;
	border-radius: 16px;
	padding: 20px;

	&:not(.active):hover {
		border-color: $slate-100;
	}

	@include breakpoint(lg) {
		padding: 32px;
	}

	&.active {
		background-color: $black;
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			z-index: -1;
			margin: -1px;
			border-radius: inherit;
			background: linear-gradient(to right, $blue-300, $pink);
		}

		.faq-answer {
			height: 100%;
			padding-top: 16px;
			opacity: 1;
		}
	}
	&.inactive {
		border: solid 1px #666d80;
	}

	.faq-answer {
		height: 0;
		padding: 0;
		overflow: hidden;
		transition: 200ms ease-in-out all;
		opacity: 0;
	}
}

.article-body {
	display: flex;
	gap: 24px;
	flex-direction: column;

	ul {
		display: flex;
		gap: 24px;
		flex-direction: column;
		list-style: circle;
	}
}
.faq-question {
	font-size: 1.25rem;
	color: white;
	text-align: left;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	font-family: "DM Sans", sans-serif;
}

.faq-answer {
	padding-bottom: 20px;
	color: white;
	font-size: 1rem;
	a {
		color: $blue-300;
	}
}

.chevron-icon {
	transition: transform 0.3s ease;
}
</style>
